import { logger } from 'utils'

const DEFAULT_COOKIE_NAME = 'eight-wave-ai'
const DEFAULT_USER_ID_COOKIE_SUFFIX = '-uid'
const MAX_AGE_ONE_YEAR = 31536000

function setCookie(name: string, value: string, maxAge: number) {
    try {
        // A past `expires` accompanies `max-age=0` on deletion: some cookie
        // implementations (older browsers, happy-dom) only honor the former.
        const expires =
            maxAge > 0 ? '' : '; expires=Thu, 01 Jan 1970 00:00:00 GMT'
        document.cookie = `${name}=${value}; path=/; max-age=${maxAge}${expires}; SameSite=Lax; Secure`
    } catch (error) {
        logger.warn(
            '[cookie] setCookie blocked (cross-origin/tracking protection)',
            error,
        )
    }
}

function getCookie(name: string): string {
    try {
        // Only split on the FIRST "=" — session tokens are signed with a
        // base64 signature that can contain "=" padding, so splitting on every
        // "=" would truncate the value and corrupt the token.
        const prefix = `${name}=`
        const row = document.cookie
            .split('; ')
            .find((entry) => entry.startsWith(prefix))
        return row ? row.slice(prefix.length) : ''
    } catch (error) {
        logger.warn(
            '[cookie] getCookie blocked (cross-origin/tracking protection)',
            error,
        )
        return ''
    }
}

// create first-party session cookie for authentication with 1 year expiry;
// an empty token (sign-out) deletes the cookie instead of storing an empty value
export function setSessionCookie(
    token: string,
    cookieName: string = DEFAULT_COOKIE_NAME,
) {
    setCookie(cookieName, token, token ? MAX_AGE_ONE_YEAR : 0)
}

// retrieve first-party session cookie
export function getSessionCookie(cookieName: string = DEFAULT_COOKIE_NAME) {
    return getCookie(cookieName)
}

// persist anonymous userId alongside session token to enable re-auth after session expiry
export function setAnonymousUserIdCookie(
    userId: string,
    cookieName: string = DEFAULT_COOKIE_NAME,
) {
    setCookie(
        `${cookieName}${DEFAULT_USER_ID_COOKIE_SUFFIX}`,
        userId,
        MAX_AGE_ONE_YEAR,
    )
}

// retrieve persisted anonymous userId
export function getAnonymousUserIdCookie(
    cookieName: string = DEFAULT_COOKIE_NAME,
) {
    return getCookie(`${cookieName}${DEFAULT_USER_ID_COOKIE_SUFFIX}`)
}

// remove the persisted anonymous userId once the anonymous user links a real
// account (the linked anonymous user is deleted server-side, so it's stale)
export function clearAnonymousUserIdCookie(
    cookieName: string = DEFAULT_COOKIE_NAME,
) {
    setCookie(`${cookieName}${DEFAULT_USER_ID_COOKIE_SUFFIX}`, '', 0)
}
